import {
  EnvironmentProviders,
  Provider,
  inject,
  provideAppInitializer
} from '@angular/core';
import {
  createEkoLanguagePersistHandler,
  createEkoLanguageProfileReader,
  createEkoThemePersistHandler,
  createEkoThemeProfileReader,
  EKO_LANGUAGE_PERSIST_HANDLER,
  EKO_LANGUAGE_PROFILE_READER,
  EKO_THEME_PERSIST_HANDLER,
  EKO_THEME_PROFILE_READER,
  EkoLanguageService,
  EkoThemeService,
  invalidateKeycloakProfileCache
} from '@ekowitryna/ui';
import { AccountService } from '@mobiletract/account-service-angular-client';
import Keycloak from 'keycloak-js';
import type { KeycloakInstance } from 'keycloak-js';
import { firstValueFrom } from 'rxjs';

const KEYCLOAK_INIT_TIMEOUT_MS = 12000;
const KEYCLOAK_INIT_POLL_MS = 100;
const AVAILABLE_LANGUAGES = ['pl', 'en', 'de', 'es', 'uk', 'fr'] as const;

export function provideEkoPreferencesSync(): Array<Provider | EnvironmentProviders> {
  return [
    {
      provide: EKO_THEME_PROFILE_READER,
      useFactory: (keycloak: KeycloakInstance) =>
        createEkoThemeProfileReader(async () => {
          if (!keycloak.authenticated) {
            return undefined;
          }
          invalidateKeycloakProfileCache(keycloak);
          return keycloak.loadUserProfile();
        }),
      deps: [Keycloak]
    },
    {
      provide: EKO_THEME_PERSIST_HANDLER,
      useFactory: (accountService: AccountService) =>
        createEkoThemePersistHandler(async (theme) => {
          await firstValueFrom(accountService.setTheme({ theme }));
        }),
      deps: [AccountService]
    },
    {
      provide: EKO_LANGUAGE_PROFILE_READER,
      useFactory: (keycloak: KeycloakInstance) =>
        createEkoLanguageProfileReader(async () => {
          if (!keycloak.authenticated) {
            return undefined;
          }
          invalidateKeycloakProfileCache(keycloak);
          return keycloak.loadUserProfile();
        }),
      deps: [Keycloak]
    },
    {
      provide: EKO_LANGUAGE_PERSIST_HANDLER,
      useFactory: (accountService: AccountService, keycloak: KeycloakInstance) =>
        createEkoLanguagePersistHandler(async (locale: string) => {
          if (!keycloak.authenticated) {
            return;
          }
          await firstValueFrom(accountService.setLocale({ locale }));
        }),
      deps: [AccountService, Keycloak]
    },
    provideAppInitializer(async () => {
      const keycloak = inject(Keycloak);
      const theme = inject(EkoThemeService);
      const language = inject(EkoLanguageService);
      await waitForKeycloakInitialization(keycloak);

      if (keycloak.authenticated) {
        return;
      }

      theme.initFromStorageOrSystem();
      language.initializeLanguage([...AVAILABLE_LANGUAGES]);
    })
  ];
}

async function waitForKeycloakInitialization(keycloak: KeycloakInstance): Promise<void> {
  if (keycloak.didInitialize) {
    return;
  }

  const startedAt = Date.now();
  while (!keycloak.didInitialize && Date.now() - startedAt < KEYCLOAK_INIT_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, KEYCLOAK_INIT_POLL_MS));
  }
}
